/**
 * Team management, for a superadmin.
 *
 * This is the only place in the product where accounts are created, recovered
 * or ended for good. There is no self-signup, no password-reset email, and no mailer at
 * all: an invite token and an admin-issued temporary password are handed over
 * by a human, out of band. That shapes the whole page — the two once-only
 * secrets it produces are rendered as panels that say so, and every irreversible
 * action asks first, in the page.
 *
 * The role check below is a second copy of the one in `App.tsx`, which is a
 * third copy of the one the server enforces with `require_role(Role.SUPERADMIN)`.
 * Only the server's is a security boundary. What this one buys is that a member
 * who reaches the component — a stale bundle, a future route, a mistake — is
 * told why they see nothing, and that the app does not fire an admin request it
 * already knows will 403.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api } from '../api/client'
import type { AdminUserSummary, UserDeleteResponse } from '../api/types'
import { useAuth } from '../auth/useAuth'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n'
import { describeAdminError, InviteMemberForm } from './InviteMemberForm'
import { describeDeletion, TeamMemberRow } from './TeamMemberRow'

export const TEAM_QUERY_KEY = ['admin', 'users'] as const

/** The last column carries the row actions and has no visible heading; its
 *  accessible name is rendered separately, below. */
const HEADERS: (TranslationKey | null)[] = [
  'account.team.col.email',
  'account.team.col.role',
  'account.team.col.active',
  'domain.twoFactorShort',
  'account.team.col.mustChangePassword',
  null,
]

export default function AdminPage() {
  const { t } = useI18n()
  const { user } = useAuth()
  const isSuperadmin = user?.role === 'superadmin'
  const queryClient = useQueryClient()
  /** The one outcome whose row is not there to report it. */
  const [deleted, setDeleted] = useState<string | null>(null)

  const team = useQuery({
    queryKey: TEAM_QUERY_KEY,
    queryFn: () => api.get<AdminUserSummary[]>('/api/v1/admin/users'),
    // A member must not fire this: the answer is a guaranteed 403, and a 403
    // reaching the API client is indistinguishable from a dead session.
    enabled: isSuperadmin,
    // Roles, 2FA and activity change from other tabs and other people; the
    // page is short-lived and cheap, so it re-asks on focus rather than
    // showing a snapshot from whenever it was opened.
    refetchOnWindowFocus: true,
  })

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY })
  }

  /**
   * A deleted account leaves the table here, not on the next render of a
   * refetch.
   *
   * The refetch below would drop it a moment later anyway, but a moment is
   * enough to click something on a row that no longer exists -- and every
   * button on it would answer 404. Cutting it out of the cache first makes the
   * table honest for the whole gap, and the sentence stays behind on the page,
   * because the row that would otherwise have carried it is exactly the thing
   * that was destroyed.
   */
  function handleDeleted(result: UserDeleteResponse) {
    queryClient.setQueryData<AdminUserSummary[]>(TEAM_QUERY_KEY, (rows) =>
      rows?.filter((row) => row.id !== result.user_id),
    )
    setDeleted(describeDeletion(result, t))
    invalidate()
  }

  // No <main> and no width of its own: `<AppShell wide/>` (nav/AppHeader.tsx)
  // wraps this route and owns both. A second <main> here would nest one landmark
  // inside another and lose the width argument to the outer clamp regardless.
  if (!isSuperadmin) {
    return (
      <p role="alert" className="rounded-md bg-down/10 px-3 py-2 text-sm text-down">
        {t('account.admin.denied')}
      </p>
    )
  }

  return (
    <>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-fg">{t('account.admin.title')}</h1>
        <p className="text-xs text-muted">{t('account.admin.lede')}</p>
      </header>

      <InviteMemberForm onInvited={invalidate} />

      <section className="space-y-3 rounded-lg border border-border bg-surface p-4">
        <header className="flex items-baseline gap-3">
          <h2 className="text-sm font-semibold text-fg">{t('account.admin.membersTitle')}</h2>
          {team.data && (
            <span className="text-xs text-muted">
              {t('account.admin.accountCount', { count: team.data.length })}
            </span>
          )}
        </header>

        {deleted && (
          // role="status", not role="alert": the admin asked for this and
          // watched it happen, so it is announced politely rather than
          // interrupting whatever they read next. It stays until dismissed --
          // the counts in it are the only place the invite and audit numbers
          // are ever shown.
          <div
            role="status"
            className="flex items-start justify-between gap-3 rounded-md bg-healthy/10 px-3 py-2
                       text-sm text-healthy"
          >
            <p>{deleted}</p>
            <button
              type="button"
              onClick={() => setDeleted(null)}
              className="shrink-0 text-xs text-muted hover:text-fg"
            >
              {t('account.admin.dismiss')}
            </button>
          </div>
        )}

        {team.isPending && <p className="text-sm text-muted">{t('account.admin.loading')}</p>}

        {team.isError && (
          <p role="alert" className="rounded-md bg-down/10 px-3 py-2 text-sm text-down">
            {describeAdminError(team.error, t)}
          </p>
        )}

        {team.data && (
          // Horizontal scroll on the wrapper, not the page: six columns plus
          // four action buttons do not fit a narrow window, and a page that
          // scrolls sideways moves the header with it.
          <div className="overflow-x-auto">
            <table className="w-full min-w-[58rem] border-collapse text-left">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted">
                  {HEADERS.map((header, i) => (
                    <th
                      key={header ?? `actions-${i}`}
                      scope="col"
                      className={`px-3 py-2 font-medium ${i === HEADERS.length - 1 ? 'text-right' : ''}`}
                    >
                      {header ? (
                        t(header)
                      ) : (
                        <span className="sr-only">{t('account.team.col.actions')}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {team.data.map((member) => (
                  <TeamMemberRow
                    // Keyed by id so a refetch re-renders in place: a temporary
                    // password revealed in a row must survive the list reload
                    // that the action itself triggers.
                    key={member.id}
                    user={member}
                    isSelf={member.id === user?.id}
                    onChanged={invalidate}
                    onDeleted={handleDeleted}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

export { AdminPage }
